import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL, formatDate, getStatusBadgeClass, jobTypeOptions, licenseOptions } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Plus,
  Users,
  Eye,
  Edit,
  RefreshCw,
  Trash2,
  Loader2,
  Building2,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  MoreVertical,
  Crown,
  CreditCard,
  Star,
  Sparkles,
  Lock,
  BadgeCheck,
  MessageSquare,
  Mail,
  Plug,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import EmployerInstructions from '../components/EmployerInstructions';
import EmployerTabATS from './EmployerTabATS';
import EmailPreferences from '../components/EmailPreferences';
import MessagesPanel from '../components/MessagesPanel';
import GettingStarted from '../components/GettingStarted';
import FeaturedJobAnalytics from '../components/FeaturedJobAnalytics';

const EmployerDashboard = () => {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState('confirm');
  const [deleteReason, setDeleteReason] = useState('');
  const [featureTarget, setFeatureTarget] = useState(null);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [reqVerifyLoading, setReqVerifyLoading] = useState(false);
  const [verifyRequested, setVerifyRequested] = useState(false);
  
  // Company profile
  const [companyData, setCompanyData] = useState({
    company_name: user?.company_name || '',
    company_description: user?.company_description || '',
    company_website: user?.company_website || '',
  });
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => {
    fetchJobs();
    fetchSubscription();
    if (user) {
      setCompanyData({
        company_name: user.company_name || '',
        company_description: user.company_description || '',
        company_website: user.company_website || '',
      });
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/my-subscription`, {
        headers: {  },
      });
      setSubscription(response.data);
    } catch (error) {
      console.debug('No active subscription for employer:', error?.response?.status);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs/my-jobs`, {
        headers: {  },
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (jobId) => {
    setAppsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/applications/job/${jobId}`, {
        headers: {  },
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setAppsLoading(false);
    }
  };

  const handleViewApplications = (job) => {
    setSelectedJob(job);
    fetchApplications(job.job_id);
    setActiveTab('applications');
  };

  const handleUpdateJobStatus = async (jobId, status) => {
    try {
      await axios.put(
        `${API_URL}/jobs/${jobId}/status`,
        { status },
        { headers: {  } }
      );
      setJobs(prev => prev.map(job => 
        job.job_id === jobId ? { ...job, status } : job
      ));
      toast.success('Job status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRenewJob = async (jobId, durationDays) => {
    try {
      const { data } = await axios.put(
        `${API_URL}/jobs/${jobId}/renew`,
        { duration_days: durationDays },
        { headers: {  } }
      );
      setJobs(prev => prev.map(job =>
        job.job_id === jobId
          ? { ...job, status: 'active', expiration_date: data.expiration_date }
          : job
      ));
      toast.success(durationDays ? `Job renewed for ${durationDays} days` : 'Job is now live until you close it');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await axios.delete(`${API_URL}/jobs/${jobId}`, {
        headers: {  },
      });
      setJobs(prev => prev.filter(job => job.job_id !== jobId));
      toast.success('Job deleted');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleFeatureJob = (job) => {
    setFeatureTarget(job);
  };

  const handleRequestVerification = async () => {
    setReqVerifyLoading(true);
    try {
      const res = await axios.post(`${API_URL}/employer/request-verification`, {});
      setVerifyRequested(true);
      toast.success(res.data?.message || 'Verification requested');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request verification');
    } finally {
      setReqVerifyLoading(false);
    }
  };

  const confirmFeatureJob = async () => {
    if (!featureTarget) return;
    setFeatureLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/jobs/${featureTarget.job_id}/feature`,
        { origin_url: originUrl },
        { headers: {  } }
      );
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setFeatureLoading(false);
      setFeatureTarget(null);
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.put(
        `${API_URL}/applications/${applicationId}/status`,
        { status },
        { headers: {  } }
      );
      setApplications(prev => prev.map(app =>
        app.application_id === applicationId ? { ...app, status } : app
      ));
      toast.success('Application status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setSavingCompany(true);
    try {
      await updateProfile(companyData);
      toast.success('Company profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API_URL}/users/account`, {
        headers: {  },
        data: { reason: deleteReason }
      });
      toast.success('Your account has been deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
      setDeleteStep('confirm');
      setDeleteReason('');
    }
  };

  const stats = {
    totalJobs: jobs.length,
    activeJobs: jobs.filter(j => j.status === 'active').length,
    totalApplications: jobs.reduce((sum, job) => sum + (job.application_count || 0), 0),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="text-secondary text-sm font-medium mb-1">Hire Licensed Social Workers</p>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              Employer Dashboard
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-gray-600">{user?.company_name || user?.name}</p>
              {subscription?.badge && (
                <Badge className="bg-secondary text-white text-xs">
                  {subscription.badge}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/employer-faq">
              <Button variant="ghost" className="text-gray-600 hover:text-primary" data-testid="employer-faq-btn">
                <FileText className="w-4 h-4 mr-2" />
                FAQ
              </Button>
            </Link>
            <Link to="/employer/candidates">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="browse-candidates-btn">
                <Users className="w-4 h-4 mr-2" />
                Browse Candidates
              </Button>
            </Link>
            <Link to="/employer/subscription">
              <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-white" data-testid="upgrade-btn">
                <Crown className="w-4 h-4 mr-2" />
                {subscription?.status === 'active' ? 'Manage Plan' : 'Upgrade'}
              </Button>
            </Link>
            <Link to="/employer/post-job">
              <Button className="bg-primary hover:bg-primary-hover text-white" data-testid="post-job-btn">
                <Plus className="w-4 h-4 mr-2" />
                Post New Job
              </Button>
            </Link>
          </div>
        </div>

        {/* Employer Instructions (Welcome Panel) */}
        <EmployerInstructions isCollapsed={true} />

        {/* Subscription Banner */}
        {subscription?.status === 'active' ? (
          <div className="bg-gradient-to-r from-primary to-primary-hover rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <Crown className="w-6 h-6" />
              <div>
                <p className="font-medium">{subscription.plan_name}</p>
                <p className="text-sm text-white/80">
                  Active until {new Date(subscription.end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Link to="/employer/subscription">
              <Button variant="secondary" size="sm" className="bg-white text-primary hover:bg-gray-100">
                View Plan
              </Button>
            </Link>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 border border-secondary/20 rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-secondary" />
              <div>
                <p className="font-medium text-gray-900">Unlock Premium Features</p>
                <p className="text-sm text-gray-600">
                  Get unlimited resume views, candidate alerts & more
                </p>
              </div>
            </div>
            <Link to="/employer/subscription">
              <Button className="bg-secondary hover:bg-secondary-hover text-white" data-testid="subscribe-cta">
                View Plans
              </Button>
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
                <p className="text-sm text-gray-500">Total Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
                <p className="text-sm text-gray-500">Active Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                <p className="text-sm text-gray-500">Applications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Get Verified CTA */}
        {!user?.is_verified_employer && (
          <div
            className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-transparent px-5 py-4"
            data-testid="get-verified-banner"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BadgeCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Become a Verified Employer</p>
                <p className="text-sm text-gray-600">
                  Verified employers get a trust badge on every listing and stand out to candidates — helping you attract more qualified applicants.
                </p>
              </div>
            </div>
            {(verifyRequested || user?.verification_requested) ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary shrink-0" data-testid="verification-pending">
                <CheckCircle2 className="w-4 h-4" /> Request submitted
              </span>
            ) : (
              <Button
                onClick={handleRequestVerification}
                disabled={reqVerifyLoading}
                className="bg-primary hover:bg-primary/90 text-white shrink-0"
                data-testid="request-verification-btn"
              >
                {reqVerifyLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <><BadgeCheck className="w-4 h-4 mr-2" /> Get Verified</>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Tabs */}
        <GettingStarted />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-100 p-1 mb-6">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              My Jobs
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Applications
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-1" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="company" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Company Profile
            </TabsTrigger>
            <TabsTrigger value="ats" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Plug className="w-4 h-4 mr-1" />
              ATS Integration
            </TabsTrigger>
            <TabsTrigger value="email-prefs" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="employer-tab-email-prefs">
              <Mail className="w-4 h-4 mr-1" />
              Email Preferences
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {/* Featured Social Work Job — premium upgrade + live analytics */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-5 py-4" data-testid="featured-job-upsell">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary"><Star className="w-5 h-5" /></div>
                <div>
                  <p className="font-semibold text-gray-900">Featured Social Work Job</p>
                  <p className="text-sm text-gray-600">Rank above imported jobs, add your logo &amp; an Urgently Hiring label, and invite qualified candidates. No pay-per-click.</p>
                </div>
              </div>
              <Link to="/employer/featured-job" className="shrink-0" data-testid="featured-job-upsell-cta">
                <Button className="bg-primary hover:bg-primary/90 text-white"><Star className="w-4 h-4 mr-2" /> Explore Featured Job</Button>
              </Link>
            </div>
            {jobs.filter((j) => j.is_featured && j.status === 'active').length > 0 && (
              <div className="mb-4 space-y-3" data-testid="featured-jobs-analytics">
                {jobs.filter((j) => j.is_featured && j.status === 'active').map((j) => (
                  <FeaturedJobAnalytics key={j.job_id} job={j} />
                ))}
              </div>
            )}
            {jobs.some((j) => !j.is_featured && j.status === 'active') && (
              <div
                className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-secondary/30 bg-gradient-to-r from-secondary/10 to-transparent px-5 py-4"
                data-testid="feature-nudge-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Get up to 3x more applicants</p>
                    <p className="text-sm text-gray-600">
                      {jobs.filter((j) => !j.is_featured && j.status === 'active').length} of your active listings
                      {' '}aren't featured yet. Feature a job to jump to the top of search results.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => handleFeatureJob(jobs.find((j) => !j.is_featured && j.status === 'active'))}
                  className="bg-secondary hover:bg-secondary/90 text-white shrink-0"
                  data-testid="feature-nudge-cta"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Feature a job ($49)
                </Button>
              </div>
            )}
            {jobs.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>License</th>
                        <th>Type</th>
                        <th>Applications</th>
                        <th>Status</th>
                        <th>Posted</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => (
                        <tr key={job.job_id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <Link to={`/jobs/${job.job_id}`} className="font-medium text-primary hover:underline">
                                {job.title}
                              </Link>
                              {job.is_featured && (
                                <Badge className="bg-secondary/10 text-secondary text-xs">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Featured
                                </Badge>
                              )}
                              {!job.is_featured && job.status === 'active' && (
                                <button
                                  type="button"
                                  onClick={() => handleFeatureJob(job)}
                                  data-testid={`feature-later-btn-${job.job_id}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-1 text-xs font-semibold text-secondary transition-colors hover:bg-secondary hover:text-white"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  Feature $49
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge className={getStatusBadgeClass(job.license_required === 'LMSW' ? 'info' : job.license_required === 'LCSW' ? 'active' : 'pending')}>
                              {job.license_required}
                            </Badge>
                          </td>
                          <td className="capitalize">{job.job_type.replace('-', ' ')}</td>
                          <td>
                            <button
                              onClick={() => handleViewApplications(job)}
                              className="text-primary hover:underline"
                            >
                              {job.application_count || 0}
                            </button>
                          </td>
                          <td>
                            <Badge className={getStatusBadgeClass(job.status)}>
                              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                            </Badge>
                            {!job.is_external && (
                              <div className="text-xs text-gray-500 mt-1" data-testid={`job-expiry-${job.job_id}`}>
                                {job.expiration_date
                                  ? (job.status === 'expired' ? `Expired ${formatDate(job.expiration_date)}` : `Expires ${formatDate(job.expiration_date)}`)
                                  : 'No expiry (until closed)'}
                              </div>
                            )}
                          </td>
                          <td>{formatDate(job.created_at)}</td>
                          <td>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/employer/edit-job/${job.job_id}`)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewApplications(job)}>
                                  <Users className="w-4 h-4 mr-2" /> View Applicants
                                </DropdownMenuItem>
                                {!job.is_featured && job.status === 'active' && (
                                  <DropdownMenuItem onClick={() => handleFeatureJob(job)} className="text-secondary">
                                    <Sparkles className="w-4 h-4 mr-2" /> Feature Job ($49)
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'active' ? (
                                  <DropdownMenuItem onClick={() => handleUpdateJobStatus(job.job_id, 'closed')}>
                                    <XCircle className="w-4 h-4 mr-2" /> Close Job
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleUpdateJobStatus(job.job_id, 'active')}>
                                    <CheckCircle2 className="w-4 h-4 mr-2" /> Reopen Job
                                  </DropdownMenuItem>
                                )}
                                {!job.is_external && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleRenewJob(job.job_id, 30)} data-testid={`renew-30-${job.job_id}`}>
                                      <RefreshCw className="w-4 h-4 mr-2" /> Renew — 30 days
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleRenewJob(job.job_id, 60)} data-testid={`renew-60-${job.job_id}`}>
                                      <RefreshCw className="w-4 h-4 mr-2" /> Renew — 60 days
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleRenewJob(job.job_id, 90)} data-testid={`renew-90-${job.job_id}`}>
                                      <RefreshCw className="w-4 h-4 mr-2" /> Renew — 90 days
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleRenewJob(job.job_id, null)} data-testid={`renew-open-${job.job_id}`}>
                                      <RefreshCw className="w-4 h-4 mr-2" /> Keep live until I close it
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuItem
                                  onClick={() => handleDeleteJob(job.job_id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">No jobs posted yet</h3>
                <p className="text-gray-500 mb-4">Start hiring by posting your first job.</p>
                <Link to="/employer/post-job">
                  <Button className="bg-primary hover:bg-primary-hover text-white">
                    <Plus className="w-4 h-4 mr-2" /> Post a Job
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            {selectedJob ? (
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <Button variant="ghost" onClick={() => setSelectedJob(null)}>
                    ← Back to all
                  </Button>
                  <h3 className="font-semibold text-gray-900">
                    Applications for: {selectedJob.title}
                  </h3>
                </div>

                {appsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : applications.length > 0 ? (
                  <div>
                    {/* Subscription Required Banner */}
                    {(!subscription || subscription.status !== 'active') && (
                      <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 border border-secondary/20 rounded-xl p-5 mb-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                            <Lock className="w-6 h-6 text-secondary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">Unlock Applicant Details</h4>
                            <p className="text-sm text-gray-600">
                              You have {applications.length} applicant{applications.length > 1 ? 's' : ''} waiting! 
                              Subscribe to view contact information, resumes, and connect with candidates.
                            </p>
                          </div>
                          <Link to="/employer/subscription">
                            <Button className="bg-secondary hover:bg-secondary-hover text-white">
                              <Crown className="w-4 h-4 mr-2" />
                              Choose Membership
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Applicant Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Resume</th>
                              <th>License</th>
                              <th>Applied</th>
                              <th>Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {applications.map((app) => {
                              const hasAccess = subscription?.status === 'active';
                              const isLicenseVerified = app.applicant?.license_info?.verification_status === 'verified';
                              return (
                                <tr key={app.application_id}>
                                  {/* Name - Always visible + Verified Badge */}
                                  <td className="font-medium">
                                    <div className="flex items-center gap-2">
                                      {app.applicant_name}
                                      {isLicenseVerified && (
                                        <span 
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"
                                          title="License Verified"
                                          data-testid={`verified-badge-${app.application_id}`}
                                        >
                                          <BadgeCheck className="w-3 h-3" />
                                          Verified
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {/* Email - Masked */}
                                  <td>
                                    {hasAccess ? (
                                      <a href={`mailto:${app.applicant_email}`} className="text-primary hover:underline">
                                        {app.applicant_email}
                                      </a>
                                    ) : (
                                      <span className="flex items-center gap-2 text-gray-400">
                                        <Lock className="w-3 h-3" />
                                        <span className="blur-sm select-none">email@hidden.com</span>
                                      </span>
                                    )}
                                  </td>
                                  {/* Phone - Masked */}
                                  <td>
                                    {hasAccess ? (
                                      app.phone || 'Not provided'
                                    ) : (
                                      <span className="flex items-center gap-2 text-gray-400">
                                        <Lock className="w-3 h-3" />
                                        <span className="blur-sm select-none">(xxx) xxx-xxxx</span>
                                      </span>
                                    )}
                                  </td>
                                  {/* Resume - Masked */}
                                  <td>
                                    {hasAccess ? (
                                      app.resume_url ? (
                                        <span className="text-emerald-600 text-sm">Available</span>
                                      ) : (
                                        <span className="text-gray-400 text-sm">Not uploaded</span>
                                      )
                                    ) : (
                                      <span className="flex items-center gap-2 text-gray-400">
                                        <Lock className="w-3 h-3" />
                                        <span className="text-sm">Locked</span>
                                      </span>
                                    )}
                                  </td>
                                  {/* License - Masked */}
                                  <td>
                                    {hasAccess ? (
                                      app.license_url ? (
                                        <span className="text-emerald-600 text-sm">Available</span>
                                      ) : (
                                        <span className="text-gray-400 text-sm">Not uploaded</span>
                                      )
                                    ) : (
                                      <span className="flex items-center gap-2 text-gray-400">
                                        <Lock className="w-3 h-3" />
                                        <span className="text-sm">Locked</span>
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-sm text-gray-600">{formatDate(app.created_at)}</td>
                                  {/* Status - Pending until subscribed */}
                                  <td>
                                    {hasAccess ? (
                                      <Select
                                        value={app.status}
                                        onValueChange={(value) => handleUpdateApplicationStatus(app.application_id, value)}
                                      >
                                        <SelectTrigger className="w-32">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="reviewed">Reviewed</SelectItem>
                                          <SelectItem value="interview">Interview</SelectItem>
                                          <SelectItem value="accepted">Accepted</SelectItem>
                                          <SelectItem value="rejected">Rejected</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                        Pending
                                      </span>
                                    )}
                                  </td>
                                  {/* Contact Button */}
                                  <td>
                                    {hasAccess ? (
                                      <Button variant="outline" size="sm" onClick={() => navigate(`/messages?user=${app.applicant_id}`)}>
                                        Contact
                                      </Button>
                                    ) : (
                                      <Link to="/employer/subscription">
                                        <Button variant="outline" size="sm" className="text-secondary border-secondary hover:bg-secondary/10">
                                          <Lock className="w-3 h-3 mr-1" />
                                          Contact
                                        </Button>
                                      </Link>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-medium text-gray-900 mb-2">No applications yet</h3>
                    <p className="text-gray-500">Check back later for new applications.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">Select a job to view applications</h3>
                <p className="text-gray-500">Click on the application count for any job to see applicants.</p>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <MessagesPanel userType="employer" />
          </TabsContent>

          {/* Company Profile Tab */}
          <TabsContent value="ats">
            <EmployerTabATS />
          </TabsContent>

          {/* Email Preferences Tab */}
          <TabsContent value="email-prefs">
            <EmailPreferences showJobAlerts={false} />
          </TabsContent>

          {/* Company Profile Tab */}
          <TabsContent value="company">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-6">Company Information</h3>

              <form onSubmit={handleSaveCompany} className="space-y-6 max-w-2xl">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={companyData.company_name}
                    onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                    className="mt-1"
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="company_website">Company Website</Label>
                  <Input
                    id="company_website"
                    type="url"
                    value={companyData.company_website}
                    onChange={(e) => setCompanyData({ ...companyData, company_website: e.target.value })}
                    placeholder="https://example.com"
                    className="mt-1"
                    data-testid="company-website-input"
                  />
                </div>

                <div>
                  <Label htmlFor="company_description">Company Description</Label>
                  <Textarea
                    id="company_description"
                    value={companyData.company_description}
                    onChange={(e) => setCompanyData({ ...companyData, company_description: e.target.value })}
                    placeholder="Tell job seekers about your organization..."
                    rows={4}
                    className="mt-1"
                    data-testid="company-description-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={savingCompany}
                  className="bg-primary hover:bg-primary-hover text-white"
                  data-testid="save-company-btn"
                >
                  {savingCompany ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Company Profile'
                  )}
                </Button>
              </form>

              {/* Delete Account Section */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Delete Account</h3>
                
                {!showDeleteConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    data-testid="delete-account-btn"
                  >
                    Delete My Account
                  </Button>
                ) : deleteStep === 'confirm' ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium mb-4">
                      Are you sure you want to delete your account?
                    </p>
                    <div className="flex gap-3">
                      <Button
                        variant="destructive"
                        onClick={() => setDeleteStep('reason')}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Yes, Delete
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteStep('confirm');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-gray-800 font-medium mb-3">
                      We're sorry to see you go. Why are you leaving?
                    </p>
                    <Textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder="Your feedback helps us improve (optional)"
                      className="mb-4"
                      rows={3}
                    />
                    <div className="flex gap-3">
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="confirm-delete-btn"
                      >
                        {deletingAccount ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Delete Account'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteStep('confirm');
                          setDeleteReason('');
                        }}
                        disabled={deletingAccount}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!featureTarget} onOpenChange={(open) => { if (!open && !featureLoading) setFeatureTarget(null); }}>
        <DialogContent data-testid="feature-job-dialog" className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-secondary" />
              Feature this job for $49
            </DialogTitle>
            <DialogDescription className="pt-1">
              You're about to feature <span className="font-semibold text-gray-900">{featureTarget?.title}</span> for <span className="font-semibold text-gray-900">30 days</span>. This is a one-time $49 charge.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {[
              'Top placement in search results for 30 days',
              'Featured badge highlighting your listing',
              'Up to 3x more qualified applicants',
              'Email receipt with your featured-until date',
            ].map((benefit) => (
              <li key={benefit} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-secondary shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">You'll be redirected to secure Stripe checkout. Your job stays live either way.</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFeatureTarget(null)}
              disabled={featureLoading}
              data-testid="feature-dialog-cancel"
            >
              Not now
            </Button>
            <Button
              onClick={confirmFeatureJob}
              disabled={featureLoading}
              className="bg-secondary hover:bg-secondary/90 text-white"
              data-testid="feature-dialog-confirm"
            >
              {featureLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
              ) : (
                <>Continue to payment ($49)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployerDashboard;
